import { useTranslation } from "@i18n";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useRef, useState } from "react";
import IconComponent from "../../cubeComponent/icons";
import { getUrlForImage } from "../../cubeComponent/constants";
import { useSelector } from "react-redux";
import { getImageUrl } from "@Utils";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import { groupByBucket } from "./dateBuckets";
import CheckBoxComponent from "@CubeComponents/Mui_Form/CheckBox";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";

function ImageWithFallback({ imageUrl, defaultImage }: any) {
  const [imgSrc, setImgSrc] = useState<any>(imageUrl);
  const handleError = () => {
    setImgSrc(defaultImage);
  };

  return (
    <img className="w-full h-full rounded-full object-cover" src={imgSrc} onError={handleError} alt="projectImage" />
  );
}

function NotificationItem({
  notification,
  layout,
  t,
  isFirst,
  isHovered,
  isSelected,
  iconTypeMap,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onToggleSelect,
  onDismiss,
}: any) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      setOverflows(textRef.current.scrollHeight > textRef.current.clientHeight + 1);
    }
  }, [notification.text]);

  let cursorClass = "";
  if (isHovered) {
    cursorClass = notification.redirectUrl ? "cursor-pointer" : "cursor-not-allowed";
  }

  return (
    <div
      className={`flex items-start gap-6 px-6 py-6 transition-colors ${isFirst ? "" : "border-t border-[#c6d8ec]"} ${
        isSelected ? "bg-[#e5f6ff]" : isHovered ? "bg-[#f7faff]" : ""
      }`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect(notification.id);
        }}
        title={isSelected ? (t("Deselect") as unknown as string) : (t("Select") as unknown as string)}
        className={`relative shrink-0 size-12 flex items-center justify-center overflow-hidden rounded-full cursor-pointer transition-transform duration-150 ease-out active:scale-90 ${
          isSelected ? "bg-[#caecff]" : "border-2 border-[#6bbbe3] bg-blue-50"
        }`}
      >
        {isSelected ? (
          <CheckBoxComponent checked sx={{ p: 0, pointerEvents: "none" }} />
        ) : notification.type === "Project" ? (
          <ImageWithFallback
            imageUrl={getUrlForImage({ id: `${notification.projectId}` }, "projectImage")}
            defaultImage={`${getImageUrl() + layout.PROJECT_LOGO}`}
          />
        ) : (
          <IconComponent
            className="w-6 h-6"
            name={iconTypeMap[notification.type] || "DefaultIcon"}
            width="1.5rem"
            height="1.5rem"
          />
        )}
      </button>

      <div className="flex flex-1 min-w-0 items-center gap-4 self-stretch">
        <button
          type="button"
          onClick={onClick}
          className={`flex flex-col gap-2 flex-1 min-w-0 text-start ${cursorClass}`}
          key={notification.id}
        >
          <div
            ref={textRef}
            className="text-sm text-[#192048]"
            style={{
              display: "-webkit-box",
              WebkitLineClamp: expanded ? "unset" : 4,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {notification.text}
          </div>
          <Text className="text-xs text-[#666a84]">{notification.time}</Text>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          {(overflows || expanded) && (
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setExpanded((v) => !v);
              }}
              className="shrink-0"
            >
              <IconComponent
                name="arrowDownSm"
                width="1rem"
                height="1rem"
                className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
              />
            </ButtonComponent>
          )}
          {isHovered && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss?.(e);
              }}
              className="shrink-0 flex items-center justify-center p-1 rounded cursor-pointer hover:bg-[#e8f1fc]"
              title={t("Dismiss") as unknown as string}
            >
              <IconComponent name="Cancel" width="1rem" height="1rem" className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface NotificationCardProps {
  notifications: any[];
  iconTypeMap: { [key: string]: string };
  dismissNotification: (id: string) => void;
  closeNotificationModal: () => void;
  onMarkGroupRead: (bucketKey: string, ids: string[]) => void;
  selectedIds: Set<string>;
  onToggleSelect: (id: string) => void;
}

function NotificationCard({
  notifications,
  iconTypeMap,
  dismissNotification,
  closeNotificationModal,
  onMarkGroupRead,
  selectedIds,
  onToggleSelect,
}: NotificationCardProps) {
  const { t } = useTranslation();
  const layout = useSelector((state: RootState) => state.layout);
  const navigate = useNavigate();

  const [hoveredNotification, setHoveredNotification] = useState<string | null>(null);

  const bucketedGroups = useMemo(() => groupByBucket(notifications, t), [notifications, t]);

  const handleMouseEnter = (notification: any) => {
    setHoveredNotification(notification.id);
  };

  const handleMouseLeave = () => {
    setHoveredNotification(null);
  };

  const handleRedirect = (redirectUrl: any) => {
    navigate(redirectUrl);
    closeNotificationModal();
  };

  const notificationClicked = (notification: any) => {
    if (notification.redirectUrl) {
      if (notification.read === "NOTIFICATION_UNREAD") {
        dismissNotification(notification.id);
      }
      handleRedirect(notification.redirectUrl);
    }
  };

  const handleDismiss = (e: any, notificationId: any) => {
    e.stopPropagation();
    dismissNotification(notificationId);
  };

  return (
    <div className="w-full flex flex-col gap-4 p-4">
      {bucketedGroups.map(({ bucket, items }) => {
        const groupIds = items.map((notification: any) => notification.id);
        return (
          <div key={bucket.key} className="flex flex-col">
            <div className="sticky top-4 z-10 flex h-14 items-center justify-between gap-4 rounded-t-lg border border-[#c6d8ec] bg-white px-6 py-2">
              <Text className="text-base font-semibold text-[#192048] whitespace-nowrap">{bucket.label}</Text>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkGroupRead(bucket.key, groupIds);
                }}
                className="shrink-0 h-8 px-4 rounded bg-[#e8f1fc] text-sm font-semibold text-[#192048] cursor-pointer transition hover:brightness-95"
              >
                {t("Mark as read")}
              </button>
            </div>
            <div className="border-x border-b border-[#c6d8ec] rounded-b-lg bg-white">
              <div className="overflow-hidden rounded-b-lg">
                {items.map((notification: any, index: number) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    layout={layout}
                    t={t}
                    iconTypeMap={iconTypeMap}
                    isFirst={index === 0}
                    isHovered={hoveredNotification === notification.id}
                    isSelected={selectedIds.has(notification.id)}
                    onMouseEnter={() => handleMouseEnter(notification)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => notificationClicked(notification)}
                    onToggleSelect={onToggleSelect}
                    onDismiss={(e) => handleDismiss(e, notification.id)}
                  />
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default NotificationCard;
